import type { Map as MapLibreMapType } from "maplibre-gl";
import type { TacticalFeature } from "@/services/tactical.service";

export type LngLat = [number, number];

/** Слои MVT каталога ОИ в MapLibreMap (стабильные id). */
export const TACTICAL_MVT_SNAP_LAYER_IDS = [
  "mvt-objects-fill",
  "mvt-objects-line",
  "mvt-objects-point",
  "mvt-objects-outline",
] as const;

const R = 6371000;

function toRad(d: number): number {
  return (d * Math.PI) / 180;
}

/** Расстояние между точками, м (haversine). */
export function distanceM(a: LngLat, b: LngLat): number {
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

function collectVerticesFromGeometry(g: GeoJSON.Geometry): LngLat[] {
  const out: LngLat[] = [];
  if (g.type === "Point") {
    out.push([g.coordinates[0], g.coordinates[1]]);
  } else if (g.type === "MultiPoint") {
    for (const c of g.coordinates) out.push([c[0], c[1]]);
  } else if (g.type === "LineString") {
    for (const c of g.coordinates) out.push([c[0], c[1]]);
  } else if (g.type === "MultiLineString") {
    for (const line of g.coordinates) {
      for (const c of line) out.push([c[0], c[1]]);
    }
  } else if (g.type === "Polygon") {
    for (const ring of g.coordinates) {
      for (const c of ring) out.push([c[0], c[1]]);
    }
  } else if (g.type === "MultiPolygon") {
    for (const poly of g.coordinates) {
      for (const ring of poly) {
        for (const c of ring) out.push([c[0], c[1]]);
      }
    }
  } else if (g.type === "GeometryCollection") {
    for (const child of g.geometries) {
      out.push(...collectVerticesFromGeometry(child));
    }
  }
  return out;
}

function collectSegmentsFromGeometry(g: GeoJSON.Geometry): Array<[LngLat, LngLat]> {
  const segs: Array<[LngLat, LngLat]> = [];
  const pushLine = (coords: number[][]) => {
    for (let i = 0; i < coords.length - 1; i++) {
      segs.push([
        [coords[i][0], coords[i][1]],
        [coords[i + 1][0], coords[i + 1][1]],
      ]);
    }
  };
  if (g.type === "LineString") pushLine(g.coordinates);
  else if (g.type === "MultiLineString") {
    for (const line of g.coordinates) pushLine(line);
  } else if (g.type === "Polygon") {
    for (const ring of g.coordinates) pushLine(ring);
  } else if (g.type === "MultiPolygon") {
    for (const poly of g.coordinates) {
      for (const ring of poly) pushLine(ring);
    }
  } else if (g.type === "GeometryCollection") {
    for (const child of g.geometries) {
      segs.push(...collectSegmentsFromGeometry(child));
    }
  }
  return segs;
}

/** Ближайшая точка на отрезке AB к P (в lng/lat, локальная эквидистанта). */
function nearestOnSegment(p: LngLat, a: LngLat, b: LngLat): LngLat {
  const cosLat = Math.cos(toRad(p[1]));
  const ax = a[0] * cosLat;
  const ay = a[1];
  const bx = b[0] * cosLat;
  const by = b[1];
  const px = p[0] * cosLat;
  const py = p[1];
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-18) return a;
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

/**
 * Притянуть клик к вершине или ребру набора геометрий, если ближе thresholdM.
 */
export function snapPointToGeometries(
  lng: number,
  lat: number,
  geometries: GeoJSON.Geometry[],
  thresholdM = 25,
): { lng: number; lat: number; snapped: boolean; distanceM: number } {
  const p: LngLat = [lng, lat];
  let best: LngLat = p;
  let bestD = thresholdM;

  for (const g of geometries) {
    for (const v of collectVerticesFromGeometry(g)) {
      const d = distanceM(p, v);
      if (d < bestD) {
        bestD = d;
        best = v;
      }
    }
    for (const [a, b] of collectSegmentsFromGeometry(g)) {
      const q = nearestOnSegment(p, a, b);
      const d = distanceM(p, q);
      if (d < bestD) {
        bestD = d;
        best = q;
      }
    }
  }

  if (bestD < thresholdM) {
    return { lng: best[0], lat: best[1], snapped: true, distanceM: bestD };
  }
  return { lng, lat, snapped: false, distanceM: Infinity };
}

/**
 * Притянуть клик к вершине или ребру фич доски, если ближе thresholdM.
 */
export function snapPoint(
  lng: number,
  lat: number,
  features: TacticalFeature[],
  thresholdM = 25,
): { lng: number; lat: number; snapped: boolean } {
  const geometries = features
    .map((f) => f.geometry)
    .filter((g): g is NonNullable<typeof g> => !!g);
  const r = snapPointToGeometries(lng, lat, geometries, thresholdM);
  return { lng: r.lng, lat: r.lat, snapped: r.snapped };
}

/**
 * Геометрии MVT underlay вокруг точки (для snap). Пусто, если слои не на карте.
 */
export function queryMvtGeometriesNear(
  map: MapLibreMapType,
  lng: number,
  lat: number,
  thresholdM: number,
): GeoJSON.Geometry[] {
  const zoom = map.getZoom();
  const metersPerPx =
    (40075016.686 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom + 8);
  const padPx = Math.max(12, Math.min(80, thresholdM / Math.max(metersPerPx, 0.05)));
  const pt = map.project([lng, lat]);
  const layers = TACTICAL_MVT_SNAP_LAYER_IDS.filter((id) => !!map.getLayer(id));
  if (layers.length === 0) return [];
  try {
    const feats = map.queryRenderedFeatures(
      [
        [pt.x - padPx, pt.y - padPx],
        [pt.x + padPx, pt.y + padPx],
      ],
      { layers: [...layers] },
    );
    const out: GeoJSON.Geometry[] = [];
    for (const f of feats) {
      if (f.geometry) out.push(f.geometry as GeoJSON.Geometry);
    }
    return out;
  } catch {
    return [];
  }
}

/**
 * Snap к фичам доски и опционально к MVT underlay — берётся ближайшая цель.
 */
export function snapPointWithOptionalMvt(
  lng: number,
  lat: number,
  features: TacticalFeature[],
  thresholdM: number,
  map: MapLibreMapType | null,
  snapToMvt: boolean,
): { lng: number; lat: number; snapped: boolean } {
  const board = snapPointToGeometries(
    lng,
    lat,
    features.map((f) => f.geometry).filter((g): g is NonNullable<typeof g> => !!g),
    thresholdM,
  );
  if (!snapToMvt || !map) {
    return { lng: board.lng, lat: board.lat, snapped: board.snapped };
  }
  const mvtGeoms = queryMvtGeometriesNear(map, lng, lat, thresholdM);
  const mvt = snapPointToGeometries(lng, lat, mvtGeoms, thresholdM);
  if (!board.snapped && !mvt.snapped) return { lng, lat, snapped: false };
  if (!mvt.snapped) return { lng: board.lng, lat: board.lat, snapped: true };
  if (!board.snapped) return { lng: mvt.lng, lat: mvt.lat, snapped: true };
  return board.distanceM <= mvt.distanceM
    ? { lng: board.lng, lat: board.lat, snapped: true }
    : { lng: mvt.lng, lat: mvt.lat, snapped: true };
}
